//
// Options object to pass into renderRichText
//
// We use this file to format the display of certain assets, entries,
// and hyperlinks within Rich Text fields to give Content Editors the
// flexibility they need to create dynamic content on the fly
//

/* eslint no-underscore-dangle: 0 */
/* eslint  dot-notation: 0 */
/* eslint  no-nested-ternary: 0 */
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import marksClassBuilder from '@utils/marksClassBuilder';
import { Button } from 'reactstrap';
import CardButton from '@components/CardButton';
import Link from '@components/Link';
import constructRelativeLink from '@utils/construct-relative-link';
import cx from 'classnames';

import './index.scss';

const rendererOptions = {
  // Ensure line breaks are converted to html.
  renderText: (text1) => text1.split('\n').flatMap((text2, i) => [i > 0 && <br />, text2]),
  renderNode: {
    // Render embedded images from richText fields
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { data } = node;
      const { target } = data;

      // Check if this asset has fields or not
      if (target) {
        const { file, description } = target;

        if ((file.contentType === 'image/png' || file.contentType === 'image/jpeg')) {
          return (
            <img
              alt={(description) || 'embedded media asset'}
              className="img-embed sponsor-center"
              data-cy="gatsby-img"
              src={target.gatsbyImageData['images']['fallback']['src']}
              title="asset"
              style={{ width: '100%', paddingLeft: '5px' }}
            />
          );
        }

        // Render download link for embedded .pdfs
        if (file.contentType === 'application/pdf') {
          return <a href={file.url}>Download</a>;
        }
      }

      return null;
    },
    // Render embedded blue buttons
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const { data } = node;
      const { target } = data;

      if (target) {
        if (target.__typename === 'ContentfulPrimaryButton') {
          const { buttonText, buttonUrl, newTab } = data.target;

          return (
            <Link to={buttonUrl} newTab={newTab}>
              <Button size="lg" className="btn-primary">{buttonText}</Button>
            </Link>
          );
        }
        // Render CardLinks
        if (target.__typename === 'ContentfulCardLink') {
          const {
            banner, link, tagline, titleDisplay, newTab,
          } = target;
          return (
            <CardButton
              altText={(banner && banner.description) ? banner.description : ''}
              body={(tagline) || ''}
              imgSrc={(banner) || null}
              link={link}
              title={titleDisplay}
              titleSize="h3"
              newTab={newTab}
            />
          );
        }
        // Render SponsorLogo
      }

      return null;
    },
    // Render embedded blue buttons, inline
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const { data } = node;
      const { target } = data;
      if (target) {
        if (target.__typename === 'ContentfulPrimaryButton') {
          const { buttonText, buttonUrl, newTab } = data.target;

          return (
            <Link to={buttonUrl} newTab={newTab}>
              <Button size="lg" className="btn-primary btn-inline mb-4 mb-xl-0">
                {buttonText}
              </Button>
            </Link>
          );
        }
        // Render SponsorLogo
        if (target.__typename === 'ContentfulSponsorLogo') {
          return (
            <GatsbyImage
              alt={target.logoImage && target.logoImage.description === ''
                ? 'Sponsor logo'
                : target.logoImage.description}
              image={getImage(target.logoImage)}
              imgStyle={{ objectFit: 'contain', padding: '1px' }}
              style={{ maxWidth: target.width || 125 }}
              className="sponsor-center"
            />
          );
        }

        if (target.__typename === 'ContentfulMediaImageAligned') {
          const floatDirection = target.justificationSetting === 'left' ? 'left' : 'right';
          return (
            <img
              alt={target.image && target.image.description}
              src={target.image?.gatsbyImageData['images']['fallback']['src']}
              title="image"
              className={cx(floatDirection === 'left' ? 'float-left' : 'float-right')}
            />
          );
        }
      }

      return null;
    },
    // Swap in Gatsby Links when appropriate
    [INLINES.HYPERLINK]: (node) => {
      const { content, data: { uri } } = node;

      // When possible, convert to internal relative link
      const linkTo = constructRelativeLink(uri);

      // Allow embedded videos
      if ((uri).includes('player.vimeo.com/video')) {
        return (
          <div className="iframe-container">
            <iframe
              title={`embedded video at ${uri}`}
              src={uri}
              frameBorder="0"
              allowFullScreen
            />
          </div>
        );
      }

      if ((uri).includes('youtube.com/embed')) {
        return (
          <div className="iframe-container">
            <iframe
              title={`embedded video at ${uri}`}
              src={uri}
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
              frameBorder="0"
              allowFullScreen
            />
          </div>
        );
      }

      return (
        <Link to={`${linkTo}`}>
          {content.map((item) => (
            <span className={marksClassBuilder(item)} key={uuidv4()}>{item.value}</span>))}
        </Link>
      );
    },
    [INLINES.ASSET_HYPERLINK]: (node) => {
      const { content, data } = node;
      const { target } = data;

      return (
        <Link to={target.file.url}>
          {content.map((item) => (
            <span className={marksClassBuilder(item)} key={uuidv4()}>{item.value}</span>))}
        </Link>
      );
    },
    [INLINES.ENTRY_HYPERLINK]: (node) => {
      const { content, data: { target } } = node;

      // List of Content Models whose entries live at '/'
      const rootUrlContentModels = [
        'ContentfulLandingPageSections',
        'ContentfulPresentationList',
        'ContentfulPresentationPage',
      ];

      if (target) {
        // Check if the target url is one step down from '/'
        // TODO: catch Activities, Blogs, MediaFeatures, etc.
        if (rootUrlContentModels.includes(target.__typename)) {
          return (
            <Link to={`/${target.slug}`}>
              {content.map((item) => (
                <span className={marksClassBuilder(item)} key={uuidv4()}>{item.value}</span>))}
            </Link>
          );
        }
      }

      return null;
    },
  },
};

export default rendererOptions;
